import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/app/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Shield, Users, MessageSquare, Euro, ShieldAlert, Bell, HardDrive, ScanSearch, Mail } from "lucide-react";
import AdminExtractionQualityTab from "../components/admin/AdminExtractionQualityTab";
import AdminBroadcastTab from "../components/admin/AdminBroadcastTab";
import GroupEmailComposer from "../components/admin/GroupEmailComposer";
import AdminStatsTab from "../components/admin/AdminStatsTab";
import AdminUsersTab from "../components/admin/AdminUsersTab";
import AdminUserSheet from "../components/admin/AdminUserSheet";
import AdminTransactionsTab from "../components/admin/AdminTransactionsTab";
import AdminRfqsTab from "../components/admin/AdminRfqsTab";
import AdminFilesTab from "../components/admin/AdminFilesTab";
import AdminNotificationsTab from "../components/admin/AdminNotificationsTab";

export default function AdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [openUserId, setOpenUserId] = useState(null);
  const [userQuery, setUserQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [txns, setTxns] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [files, setFiles] = useState([]);
  const [filesTotal, setFilesTotal] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ new: 0, in_contact: 0, negotiation: 0, won: 0, lost: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusDraft, setStatusDraft] = useState({ status: "in_contact", note: "" });
  const [extractionQuality, setExtractionQuality] = useState(null);
  const [extractionDays, setExtractionDays] = useState(90);
  const [error, setError] = useState("");

  const TABS = [
    { key: "stats",        label: t("admin.tabStats"),  icon: Shield },
    { key: "users",        label: t("admin.tabUsers"),  icon: Users },
    { key: "transactions", label: t("admin.tabTxns"),   icon: Euro },
    { key: "rfqs",         label: t("admin.tabRfqs"),   icon: MessageSquare },
    { key: "files",        label: t("admin.tabFiles"),  icon: HardDrive },
    { key: "extraction",   label: t("admin.tabExtraction"), icon: ScanSearch },
    { key: "email",        label: t("admin.tabEmail"),  icon: Mail },
    { key: "notifications", label: t("admin.tabNotifs"), icon: Bell },
  ];

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/dashboard");
  }, [user, navigate]);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQuery(userQuery.trim()), 300);
    return () => clearTimeout(h);
  }, [userQuery]);

  useEffect(() => {
    if (tab === "stats")        api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => setError(e.message));
    if (tab === "users")        api.get("/admin/users", { params: { q: debouncedQuery, limit: 500 } }).then((r) => setUsers(r.data || [])).catch((e) => setError(e.message));
    if (tab === "transactions") api.get("/admin/transactions").then((r) => setTxns(r.data || [])).catch((e) => setError(e.message));
    if (tab === "rfqs")         api.get("/admin/rfqs").then((r) => setRfqs(r.data || [])).catch((e) => setError(e.message));
    if (tab === "files")        api.get("/admin/files").then((r) => { setFiles(r.data?.files || []); setFilesTotal(r.data?.total || 0); }).catch((e) => setError(e.message));
    if (tab === "extraction")   api.get("/admin/extraction-quality", { params: { days: extractionDays } }).then((r) => setExtractionQuality(r.data)).catch((e) => setError(e.message));
    if (tab === "notifications") api.get("/admin/notifications", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => {
      setNotifs(r.data?.items || []);
      setNotifUnread(r.data?.unread_count || 0);
      if (r.data?.status_counts) setStatusCounts(r.data.status_counts);
    }).catch((e) => setError(e.message));
  }, [tab, statusFilter, extractionDays, debouncedQuery]);

  const markNotifRead = async (id) => {
    try {
      await api.put(`/admin/notifications/${id}/read`);
      setNotifs((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setNotifUnread((u) => Math.max(0, u - 1));
    } catch (e) { alert(e.response?.data?.detail || t("common.error")); }
  };

  const markAllNotifsRead = async () => {
    try {
      await api.put("/admin/notifications/read-all");
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
      setNotifUnread(0);
    } catch (e) { alert(e.response?.data?.detail || t("common.error")); }
  };

  const openStatusEdit = (n) => {
    setEditingStatusId(n.id);
    setStatusDraft({ status: n.status === "new" ? "in_contact" : (n.status || "in_contact"), note: "" });
  };
  const cancelStatusEdit = () => { setEditingStatusId(null); setStatusDraft({ status: "in_contact", note: "" }); };
  const saveStatus = async (id) => {
    try {
      const { data } = await api.put(`/admin/notifications/${id}/status`, statusDraft);
      setNotifs((prev) => prev.map((n) => n.id === id ? data : n));
      // refresh counters from server next tick by re-querying
      api.get("/admin/notifications", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => {
        if (r.data?.status_counts) setStatusCounts(r.data.status_counts);
        setNotifUnread(r.data?.unread_count || 0);
      }).catch(() => {});
      setEditingStatusId(null);
    } catch (e) {
      alert(e.response?.data?.detail || t("common.error"));
    }
  };

  const setUserTier = async (uid, newTier) => {
    if (!window.confirm(t("admin.tierChangeConfirm", { tier: newTier }))) return;
    try {
      // Show what the server actually stored, not an optimistic local value.
      const { data } = await api.put(`/admin/users/${uid}/tier`, { tier: newTier });
      setUsers((prev) => prev.map((u) => u.id === uid ? { ...u, tier: data.plan.tier, plan: data.plan } : u));
    } catch (e) { alert(e.response?.data?.detail || t("common.error")); }
  };

  const deleteRfq = async (id) => {
    if (!window.confirm(t("admin.rfqDeleteConfirm"))) return;
    try {
      await api.delete(`/admin/rfqs/${id}`);
      setRfqs((prev) => prev.filter((r) => r.id !== id));
    } catch (e) { alert(e.response?.data?.detail || t("common.error")); }
  };

  if (user && user.role !== "admin") return null;

  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-7xl mx-auto" data-testid="admin-page">
        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert size={20} className="text-amber-400"/>
          <div className="label-tech text-amber-400">{t("admin.label")}</div>
        </div>
        <h1 className="font-display font-bold text-3xl text-slate-100 mb-6">{t("admin.title")}</h1>

        <div className="border-b border-slate-800 mb-6 flex items-center gap-1 overflow-x-auto">
          {TABS.map((tt) => {
            const Icon = tt.icon; const active = tab === tt.key;
            const badge = tt.key === "notifications" && notifUnread > 0 ? notifUnread : null;
            return (
              <button key={tt.key} onClick={() => setTab(tt.key)}
                className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${active ? "border-amber-500 text-amber-300" : "border-transparent text-slate-400 hover:text-slate-200"}`}
                data-testid={`admin-tab-${tt.key}`}>
                <Icon size={14}/> {tt.label}
                {badge !== null && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 text-[10px] font-mono-data border border-red-500/40">{badge}</span>
                )}
              </button>
            );
          })}
        </div>

        {error && <div className="mb-4 text-sm text-red-400" data-testid="admin-error">{error}</div>}

        {tab === "stats" && stats && <AdminStatsTab stats={stats} t={t} />}
        {tab === "users" && <AdminUsersTab users={users} query={userQuery} onQueryChange={setUserQuery} onSetTier={setUserTier} onOpenUser={setOpenUserId} t={t} />}
        <AdminUserSheet
          target={users.find((u) => u.id === openUserId) || null}
          onClose={() => setOpenUserId(null)}
          onSetTier={setUserTier}
          onDeleted={(uid) => { setUsers((prev) => prev.filter((u) => u.id !== uid)); setOpenUserId(null); }}
          t={t}
        />
        {tab === "transactions" && <AdminTransactionsTab txns={txns} t={t} />}
        {tab === "rfqs" && <AdminRfqsTab rfqs={rfqs} onDelete={deleteRfq} t={t} />}
        {tab === "files" && <AdminFilesTab files={files} filesTotal={filesTotal} />}
        {tab === "extraction" && (
          <AdminExtractionQualityTab
            data={extractionQuality}
            days={extractionDays}
            onDaysChange={setExtractionDays}
          />
        )}
        {tab === "email" && (
          <div className="space-y-10">
            <GroupEmailComposer />
            <div className="border-t border-slate-800 pt-8"><AdminBroadcastTab /></div>
          </div>
        )}
        {tab === "notifications" && (
          <AdminNotificationsTab
            notifs={notifs}
            notifUnread={notifUnread}
            statusCounts={statusCounts}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            editingStatusId={editingStatusId}
            statusDraft={statusDraft}
            setStatusDraft={setStatusDraft}
            onOpenEdit={openStatusEdit}
            onCancelEdit={cancelStatusEdit}
            onSaveStatus={saveStatus}
            onMarkRead={markNotifRead}
            onMarkAllRead={markAllNotifsRead}
          />
        )}
      </div>
    </AppLayout>
  );
}
